import React, { useState, useEffect } from 'react';
import { safeLocalStorage } from '@/lib/storage-utils';
import { Palette, Image, LogIn, Upload, Menu, Info, X, ChevronDown, ChevronRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { navigationItems, DEFAULT_HIDDEN_MENU_IDS } from '@/lib/navigation';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HexColorPicker } from 'react-colorful';
import { ImageUploadModal } from '@/components/brand/image-upload/ImageUploadModal';
import { supabase } from '@/lib/supabase';
import { useWorkspace } from '@/context/WorkspaceContext';
import { toast } from 'sonner';
import { uploadToR2, deleteFromR2 } from '@/lib/r2-storage';

interface Workspace {
  id: string;
  name: string;
  color?: string;
  logo?: string;
  thumbnail?: string;
  favicon?: string;
  appearance?: {
    logo?: string;
    favicon?: string;
    theme?: {
      accentColor?: string;
    };
    loginPage?: {
      backgroundImage?: string;
      backgroundColor?: string;
      logo?: string;
      title?: string;
      subtitle?: string;
      buttonColor?: string;
      textColor?: string;
    };
    menu?: {
      hiddenIds?: string[];
    };
  };
};

interface AppearanceTabProps {
  workspace: Workspace;
  isGlobalDarkMode: boolean;
}

export const AppearanceTab: React.FC<AppearanceTabProps> = ({ workspace, isGlobalDarkMode: _ }) => {
  const { refreshWorkspaces } = useWorkspace();

  const defaultTheme = {
    accentColor: '#3B82F6',
  };

  const [logo, setLogo] = useState(workspace.appearance?.logo || workspace.logo || '');
  const [bannerImage, setBannerImage] = useState((workspace as any).banner_image || '');
  const [favicon, setFavicon] = useState(workspace.favicon || workspace.appearance?.favicon || '');
  const [theme, setTheme] = useState({
    ...defaultTheme,
    ...workspace.appearance?.theme,
  });
  const [loginPage, setLoginPage] = useState(workspace.appearance?.loginPage || {
    backgroundImage: '',
    backgroundColor: '#FFFFFF',
    logo: '',
    title: 'Welcome Back',
    subtitle: 'Sign in to your account',
    buttonColor: '#3B82F6',
    textColor: '#111827',
  });

  const [logoUploadOpen, setLogoUploadOpen] = useState(false);
  const [bannerUploadOpen, setBannerUploadOpen] = useState(false);
  const [faviconUploadOpen, setFaviconUploadOpen] = useState(false);
  const [loginLogoUploadOpen, setLoginLogoUploadOpen] = useState(false);
  const [loginBgUploadOpen, setLoginBgUploadOpen] = useState(false);

  useEffect(() => {
    if (workspace.appearance) {
      if (workspace.appearance.logo) {
        setLogo(workspace.appearance.logo);
      } else if (workspace.logo) {
        setLogo(workspace.logo);
      }

      if (workspace.appearance.favicon || workspace.favicon) {
        setFavicon(workspace.appearance.favicon || workspace.favicon || '');
      }

      if (workspace.appearance.theme) {
        setTheme({
          ...defaultTheme,
          ...workspace.appearance.theme,
        });
      }

      if (workspace.appearance.loginPage) {
        setLoginPage(workspace.appearance.loginPage);
      }
    } else {
      if (workspace.logo) setLogo(workspace.logo);
      if (workspace.favicon) setFavicon(workspace.favicon);
    }

    if ((workspace as any).banner_image) {
      setBannerImage((workspace as any).banner_image);
    }

    const savedWorkspaces = safeLocalStorage.getItem('userWorkspaces');
    if (savedWorkspaces) {
      const workspaces = JSON.parse(savedWorkspaces);
      const currentWorkspace = workspaces.find((w: Workspace) => w.id === workspace.id);
      if (currentWorkspace?.appearance && !workspace.appearance) {
        setLogo(currentWorkspace.appearance.logo || currentWorkspace.logo || '');
        setFavicon(currentWorkspace.appearance.favicon || '');
        setTheme({
          ...defaultTheme,
          ...(currentWorkspace.appearance.theme || {}),
        });
        setLoginPage(currentWorkspace.appearance.loginPage || loginPage);
      }
    }
  }, [workspace.id, workspace.appearance, (workspace as any).banner_image]);

  const saveWorkspaceAppearance = (updates: Partial<Workspace['appearance']>) => {
    const savedWorkspaces = safeLocalStorage.getItem('userWorkspaces');
    if (savedWorkspaces) {
      const workspaces = JSON.parse(savedWorkspaces);
      const updatedWorkspaces = workspaces.map((w: Workspace) => {
        if (w.id === workspace.id) {
          return {
            ...w,
            appearance: {
              ...w.appearance,
              ...updates,
            },
          };
        }
        return w;
      });
      safeLocalStorage.setItem('userWorkspaces', JSON.stringify(updatedWorkspaces));
    }
  };

  const saveToDatabase = async (updatedAppearance: any) => {
    try {
      const { error } = await supabase
        .from('workspaces')
        .update({
          appearance: updatedAppearance
        })
        .eq('id', workspace.id);

      if (error) throw error;

      await refreshWorkspaces();
      return true;
    } catch (error) {
      console.error('Error saving to database:', error);
      return false;
    }
  };

  const handleLogoSelect = async (imageData: string, metadata?: any) => {
    let finalUrl = imageData;

    if (metadata?.source === 'upload' && metadata.file) {
      try {
        const uploadResponse = await uploadToR2({
          fileName: metadata.file.name,
          file: metadata.file,
          folder: `workspace_media/${workspace.id}/logo`
        });
        finalUrl = uploadResponse.publicUrl;
      } catch (error) {
        console.error('Error uploading logo to R2:', error);
        toast.error('Failed to upload logo');
        return;
      }
    }

    if (logo && logo !== finalUrl && logo.includes('agencyplus.io')) {
      await deleteFromR2(logo);
    }

    setLogo(finalUrl);
    const updatedAppearance = {
      ...workspace.appearance,
      logo: finalUrl,
      theme,
      loginPage
    };

    saveWorkspaceAppearance(updatedAppearance);

    try {
      const { error } = await supabase
        .from('workspaces')
        .update({
          logo: finalUrl,
          appearance: updatedAppearance
        })
        .eq('id', workspace.id);

      if (error) throw error;

      await refreshWorkspaces();
      toast.success('Logo updated');
    } catch (error) {
      console.error('Error updating logo:', error);
      toast.error('Failed to update logo');
    }
  };

  const handleBannerSelect = async (imageData: string, metadata?: any) => {
    let finalUrl = imageData;

    if (metadata?.source === 'upload' && metadata.file) {
      try {
        const uploadResponse = await uploadToR2({
          fileName: metadata.file.name,
          file: metadata.file,
          folder: `workspace_media/${workspace.id}/banner`
        });
        finalUrl = uploadResponse.publicUrl;
      } catch (error) {
        console.error('Error uploading banner to R2:', error);
        toast.error('Failed to upload banner image');
        return;
      }
    }

    if (bannerImage && bannerImage !== finalUrl && bannerImage.includes('agencyplus.io')) {
      await deleteFromR2(bannerImage);
    }

    setBannerImage(finalUrl);
    try {
      const { error } = await supabase
        .from('workspaces')
        .update({
          banner_image: finalUrl,
          thumbnail: finalUrl
        })
        .eq('id', workspace.id);

      if (error) throw error;
      await refreshWorkspaces();
      toast.success('Banner image and thumbnail updated');
    } catch (error) {
      console.error('Error updating banner:', error);
      toast.error('Failed to update banner image');
    }
  };

  const handleFaviconSelect = async (imageData: string, metadata?: any) => {
    let finalUrl = imageData;

    if (metadata?.source === 'upload' && metadata.file) {
      try {
        const uploadResponse = await uploadToR2({
          fileName: metadata.file.name,
          file: metadata.file,
          folder: `workspace_media/${workspace.id}/favicon`
        });
        finalUrl = uploadResponse.publicUrl;
      } catch (error) {
        console.error('Error uploading favicon to R2:', error);
        toast.error('Failed to upload favicon');
        return;
      }
    }

    if (favicon && favicon !== finalUrl && favicon.includes('agencyplus.io')) {
      await deleteFromR2(favicon);
    }

    setFavicon(finalUrl);
    const updatedAppearance = {
      ...workspace.appearance,
      favicon: finalUrl,
      theme,
      loginPage
    };

    saveWorkspaceAppearance(updatedAppearance);

    try {
      const { error } = await supabase
        .from('workspaces')
        .update({
          favicon: finalUrl,
          appearance: updatedAppearance
        })
        .eq('id', workspace.id);

      if (error) throw error;

      await refreshWorkspaces();
      toast.success('Favicon updated');
    } catch (error) {
      console.error('Error updating favicon:', error);
      toast.error('Failed to update favicon');
    }
  };

  const handleThemeChange = async (key: keyof typeof theme, value: string) => {
    const updatedTheme = { ...theme, [key]: value };
    setTheme(updatedTheme);
    const updatedAppearance = {
      ...workspace.appearance,
      theme: updatedTheme,
      loginPage,
      logo,
      favicon
    };
    saveWorkspaceAppearance(updatedAppearance);
    await saveToDatabase(updatedAppearance);

    if (key === 'accentColor') {
      const root = document.documentElement;
      root.style.setProperty('--workspace-accent', value);
      const adjustColorBrightness = (hex: string, percent: number): string => {
        const num = parseInt(hex.replace('#', ''), 16);
        const amt = Math.round(2.55 * percent);
        const R = (num >> 16) + amt;
        const G = (num >> 8 & 0x00FF) + amt;
        const B = (num & 0x0000FF) + amt;
        return '#' + (0x1000000 + (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 + (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 + (B < 255 ? (B < 1 ? 0 : B) : 255)).toString(16).slice(1).toUpperCase();
      };
      root.style.setProperty('--workspace-accent-hover', adjustColorBrightness(value, -10));
      root.style.setProperty('--workspace-accent-light', adjustColorBrightness(value, 40));
    }
  };

  const handleLoginPageChange = async (key: keyof typeof loginPage, value: string, metadata?: any) => {
    let finalValue = value;

    if (metadata?.source === 'upload' && metadata.file) {
      try {
        const uploadResponse = await uploadToR2({
          fileName: metadata.file.name,
          file: metadata.file,
          folder: `workspace_media/${workspace.id}/login`
        });
        finalValue = uploadResponse.publicUrl;
      } catch (error) {
        console.error(`Error uploading login page ${key} to R2:`, error);
        toast.error(`Failed to upload login page ${key}`);
        return;
      }
    }

    const updatedLoginPage = { ...loginPage, [key]: finalValue };
    setLoginPage(updatedLoginPage);
    const updatedAppearance = {
      ...workspace.appearance,
      loginPage: updatedLoginPage,
      theme,
      logo,
      favicon
    };
    saveWorkspaceAppearance(updatedAppearance);
    await saveToDatabase(updatedAppearance);
  };


  const ColorPickerField = ({
    label,
    value,
    onChange
  }: {
    label: string;
    value: string;
    onChange: (value: string) => void;
  }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
      <div className="space-y-2">
        <Label className="text-foreground">
          {label}
        </Label>
        <div className="flex items-center gap-3">
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <div
                className="w-16 h-10 rounded border-2 border-border cursor-pointer hover:border-muted-foreground transition-colors"
                style={{ backgroundColor: value }}
              />
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4 bg-popover border-border">
              <HexColorPicker
                color={value}
                onChange={(newColor) => onChange(newColor.toUpperCase())}
                style={{ width: '100%' }}
              />
              <div className="mt-3">
                <Input
                  value={value}
                  onChange={(e) => onChange(e.target.value.toUpperCase())}
                  className="font-mono text-sm bg-background border-input text-foreground"
                  placeholder="#000000"
                />
              </div>
            </PopoverContent>
          </Popover>
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            className="font-mono text-sm flex-1 bg-background border-input text-foreground"
            placeholder="#000000"
          />
        </div>
      </div>
    );
  };

  const ImageUploadField = ({
    label,
    description,
    value,
    onSelect,
    isOpen,
    onOpenChange,
    acceptFormats = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'],
    maxFileSize = 5,
    className = "w-20 h-20",
    imageClassName = "object-contain",
  }: {
    label: string;
    description: string;
    value: string;
    onSelect: (imageData: string, metadata?: any) => void;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    acceptFormats?: string[];
    maxFileSize?: number;
    className?: string;
    imageClassName?: string;
  }) => (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label className="text-foreground">
          {label}
        </Label>
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="w-4 h-4 cursor-pointer text-muted-foreground" />
          </TooltipTrigger>
          <TooltipContent>
            <p className="max-w-xs">{description}</p>
          </TooltipContent>
        </Tooltip>
      </div>

      <div
        className={`group relative rounded border-2 border-dashed flex items-center justify-center cursor-pointer transition-colors ${className} border-border bg-muted hover:bg-muted/80`}
        onClick={() => onOpenChange(true)}
      >
        {value ? (
          <>
            <div className="absolute inset-0 overflow-hidden rounded-[inherit]">
              <img
                src={value}
                alt={label}
                className={`w-full h-full ${imageClassName}`}
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <div className="p-2 bg-white/20 rounded-full backdrop-blur-sm text-white hover:bg-white/30 transition-colors">
                  <Upload className="w-5 h-5" />
                </div>
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSelect('');
                saveWorkspaceAppearance({});
              }}
              className="absolute -top-2 -right-2 z-20 p-1 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90 shadow-sm transition-transform hover:scale-110"
            >
              <X className="w-3 h-3" />
            </button>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center text-center p-2">
            <div className="relative">
              <Image className="w-8 h-8 text-muted-foreground group-hover:opacity-0 transition-opacity" />
              <Upload className="w-8 h-8 absolute top-0 left-0 text-workspace-accent opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
        )}
      </div>

      <ImageUploadModal
        isOpen={isOpen}
        onClose={() => onOpenChange(false)}
        onImageSelect={(imageData, metadata) => {
          onSelect(imageData, metadata);
          onOpenChange(false);
        }}
        isGlobalDarkMode={false}
        title={`Upload ${label}`}
        acceptedFormats={acceptFormats}
        maxFileSize={maxFileSize}
      />
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2 text-foreground">Appearance</h2>
        <p className="text-sm text-muted-foreground">
          Customize your workspace appearance, branding, and theme
        </p>
      </div>

      <Accordion type="single" collapsible defaultValue="media" className="w-full space-y-4">
        {/* Media Section */}
        <AccordionItem value="media" className="border rounded-lg px-4 bg-card border-border">
          <AccordionTrigger className="text-foreground hover:no-underline">
            <div className="flex items-center gap-3">
              <Image className="w-5 h-5 text-muted-foreground" />
              <span className="text-lg font-semibold">Media</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4 space-y-8">
            <div>
              <ImageUploadField
                label="Banner Image"
                description="Upload a banner image for your workspace card (recommended: 1200x400px or 3:1 aspect ratio)"
                value={bannerImage}
                onSelect={handleBannerSelect}
                isOpen={bannerUploadOpen}
                onOpenChange={setBannerUploadOpen}
                className="w-full aspect-[3/1]"
                imageClassName="object-cover"
                acceptFormats={['.jpg', '.jpeg', '.png', '.gif', '.webp']}
              />
            </div>

            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-1">
                <ImageUploadField
                  label="Logo"
                  description="Upload your workspace logo (recommended: 200x200px, PNG or SVG)"
                  value={logo}
                  onSelect={handleLogoSelect}
                  isOpen={logoUploadOpen}
                  onOpenChange={setLogoUploadOpen}
                  className="w-full h-40"
                  imageClassName="object-contain"
                />
              </div>

              <div className="flex-none">
                <ImageUploadField
                  label="Favicon"
                  description="Upload a favicon for your workspace (recommended: 32x32px or 16x16px, ICO or PNG)"
                  value={favicon}
                  onSelect={handleFaviconSelect}
                  isOpen={faviconUploadOpen}
                  onOpenChange={setFaviconUploadOpen}
                  acceptFormats={['.ico', '.png', '.jpg', '.jpeg', '.svg']}
                  maxFileSize={1}
                  className="w-40 h-40"
                  imageClassName="object-contain"
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Themes and Colors Section */}
        <AccordionItem value="themes" className="border rounded-lg px-4 bg-card border-border">
          <AccordionTrigger className="text-foreground hover:no-underline">
            <div className="flex items-center gap-3">
              <Palette className="w-5 h-5 text-muted-foreground" />
              <span className="text-lg font-semibold">Themes and Colors</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ColorPickerField
                label="Accent Color"
                value={theme.accentColor}
                onChange={(value) => handleThemeChange('accentColor', value)}
              />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Login Page Section */}
        <AccordionItem value="login" className="border rounded-lg px-4 bg-card border-border">
          <AccordionTrigger className="text-foreground hover:no-underline">
            <div className="flex items-center gap-3">
              <LogIn className="w-5 h-5 text-muted-foreground" />
              <span className="text-lg font-semibold">Login Page</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4 space-y-6">
            <ImageUploadField
              label="Background Image"
              description="Upload a background image for the login page (optional)"
              value={loginPage.backgroundImage || ''}
              onSelect={(value, metadata) => handleLoginPageChange('backgroundImage', value, metadata)}
              isOpen={loginBgUploadOpen}
              onOpenChange={setLoginBgUploadOpen}
            />
            <ColorPickerField
              label="Background Color"
              value={loginPage.backgroundColor || '#FFFFFF'}
              onChange={(value) => handleLoginPageChange('backgroundColor', value)}
            />
            <ImageUploadField
              label="Login Page Logo"
              description="Upload a logo to display on the login page (optional)"
              value={loginPage.logo || ''}
              onSelect={(value, metadata) => handleLoginPageChange('logo', value, metadata)}
              isOpen={loginLogoUploadOpen}
              onOpenChange={setLoginLogoUploadOpen}
            />
            <div className="space-y-2">
              <Label className="text-foreground">
                Login Page Title
              </Label>
              <Input
                value={loginPage.title || ''}
                onChange={(e) => handleLoginPageChange('title', e.target.value)}
                className="bg-background border-input text-foreground placeholder:text-muted-foreground"
                placeholder="Welcome Back"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">
                Login Page Subtitle
              </Label>
              <Input
                value={loginPage.subtitle || ''}
                onChange={(e) => handleLoginPageChange('subtitle', e.target.value)}
                className="bg-background border-input text-foreground placeholder:text-muted-foreground"
                placeholder="Sign in to your account"
              />
            </div>
            <ColorPickerField
              label="Button Color"
              value={loginPage.buttonColor || '#3B82F6'}
              onChange={(value) => handleLoginPageChange('buttonColor', value)}
            />
            <ColorPickerField
              label="Text Color"
              value={loginPage.textColor || '#111827'}
              onChange={(value) => handleLoginPageChange('textColor', value)}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
